import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Wallet, TrendingUp, Zap, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const SIGNUP_URL = 'https://my.tdfxcapital.net/portal';

const accounts = [
  {
    type: 'CENT',
    icon: Wallet,
    subtitle: 'Ideal for beginners',
    minDeposit: '$10',
    leverage: '1:500',
    commission: '$3.5 per side per lot',
    features: [
      'Low-cost entry into trading',
      'Competitive spreads',
      'All major instruments available',
      'Perfect for learning',
    ],
  },
  {
    type: 'PRO',
    icon: TrendingUp,
    subtitle: 'For active traders',
    minDeposit: '$1,000',
    leverage: '1:500',
    commission: 'Commission Free',
    features: [
      'Spreads from 0.7 pips',
      'Enhanced trading conditions',
      'Priority customer support',
      'Advanced trading tools',
    ],
    featured: true,
  },
  {
    type: 'ECN',
    icon: Zap,
    subtitle: 'For professional traders',
    minDeposit: '$5,000',
    leverage: '1:400',
    commission: '$3.5 per side per lot',
    features: [
      'Ultra-tight spreads from 0.5',
      'Direct market access',
      'Institutional-grade execution',
      'Dedicated account manager',
    ],
  },
];

export const AccountsSection = () => {
  const handleSignup = () => {
    window.open(SIGNUP_URL, '_blank');
  };

  return (
    <section className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            Trade With Our Accounts
          </h2>
          <p className="text-lg text-gray-600">
            Choose the account that matches your trading style and experience level.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {accounts.map((account, index) => (
            <Card
              key={index}
              className={`p-6 hover:shadow-xl transition-all duration-300 relative ${
                account.featured ? 'border-2 border-primary shadow-lg' : 'border-gray-200'
              }`}
            >
              {account.featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-primary text-white text-xs font-bold px-3 py-1 rounded-full">
                    MOST POPULAR
                  </span>
                </div>
              )}

              {/* Header */}
              <div className="flex items-center gap-4 mb-4 mt-2">
                <div className="w-14 h-14 bg-gradient-to-r from-orange-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <account.icon className="h-7 w-7 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{account.type} Account</h3>
                  <p className="text-sm text-gray-500">{account.subtitle}</p>
                </div>
              </div>

              {/* Key Info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-primary">{account.minDeposit}</div>
                    <div className="text-xs text-gray-500">Min. Deposit</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">{account.leverage}</div>
                    <div className="text-xs text-gray-500">Leverage</div>
                  </div>
                </div>
              </div>

              <div className="text-sm text-gray-600 mb-4">
                <span className="font-medium">Commission:</span> {account.commission}
              </div>

              {/* Features */}
              <ul className="space-y-2 mb-6">
                {account.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button 
                className="w-full btn-premium text-white font-semibold h-11"
                onClick={handleSignup}
              >
                Open {account.type} Account
              </Button>
            </Card>
          ))}
        </div>

        <div className="text-center mt-8">
          <Link 
            to="/accounts/compare" 
            className="text-primary font-semibold hover:underline"
          >
            Compare All Account Features →
          </Link>
        </div>
      </div>
    </section>
  );
};

export default AccountsSection;